import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FiTrash2, FiExternalLink, FiMail, FiPhone } from 'react-icons/fi';

const ManageApplications = () => {
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchApplications = async () => {
        try {
            const response = await fetch('/api/applications/read.php');
            if (response.ok) {
                const data = await response.json();
                setApplications(data.records || []);
            } else {
                throw new Error('Failed to fetch');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error('Failed to load applications');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this application?')) return;

        try {
            const response = await fetch('/api/applications/delete.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
            });

            if (response.ok) {
                toast.success('Application deleted');
                fetchApplications();
            } else {
                toast.error('Failed to delete');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to connect to server');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Job Applications</h1>
                    <p className="text-gray-500">View and manage job applications</p>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-4 font-semibold text-gray-600">Applicant</th>
                                <th className="p-4 font-semibold text-gray-600">Job</th>
                                <th className="p-4 font-semibold text-gray-600">Contact</th>
                                <th className="p-4 font-semibold text-gray-600">Resume</th>
                                <th className="p-4 font-semibold text-gray-600">Date</th>
                                <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : applications.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500">No applications yet.</td>
                                </tr>
                            ) : (
                                applications.map((app) => (
                                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <div className="font-medium text-gray-800">{app.name}</div>
                                            {app.message && (
                                                <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">{app.message}</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="px-2 py-1 bg-primary-50 text-primary-600 rounded text-xs font-semibold">
                                                {app.job_title || 'General'}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-col gap-1 text-sm">
                                                <a href={`mailto:${app.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                                    <FiMail size={14} /> {app.email}
                                                </a>
                                                {app.phone && (
                                                    <a href={`tel:${app.phone}`} className="flex items-center gap-1 text-gray-600">
                                                        <FiPhone size={14} /> {app.phone}
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {app.resume_link ? (
                                                <a
                                                    href={app.resume_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
                                                >
                                                    <FiExternalLink /> View Resume
                                                </a>
                                            ) : (
                                                <span className="text-gray-400 text-sm">No link</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-gray-500 text-sm">
                                            {formatDate(app.created_at)}
                                        </td>
                                        <td className="p-4 text-right">
                                            <button
                                                onClick={() => handleDelete(app.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ManageApplications;
